import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useWorkspace } from './use-workspace';

// Types
import { AiSuggestionWorkspaceItem, AiSuggestionWorkspaceItemType } from 'Shared/types/AiSuggestionWorkspaceItem.type';

// Actions
import {
  fetchAiSuggestionWorkspaeItems,
  createNewAiSuggestionWorkspaceeItems,
  createNewAiSuggestionWorkspaceeItemsPoll,
  acceptAiSuggestionWorkspaceItem,
  rejectAiSuggestionWorkspaceItem,
} from 'Shared/actions/AiSuggestionWorkspaceItems.actions';

type Params = {
  fetchSuggestions?: boolean;
};

export const useAiSuggestionWorkspaceItems = ({ fetchSuggestions = true }: Params = {}) => {
  const disatch = useDispatch();

  const [isFetching, toggleIsFetching] = useToggle(fetchSuggestions);
  const [isRegenerating, toggleIsRegenerating] = useToggle(false);
  const [suggestions, setSuggestions] = useState<AiSuggestionWorkspaceItem[]>([]);
  const [isReadyForGenerationPoll, setIsReadyForGenerationPoll] = useState<boolean>(false);

  const { workspace } = useWorkspace();
  const { id: workspaceId } = workspace;

  const actionItemSuggestions = suggestions.filter(item => item.type === AiSuggestionWorkspaceItemType.action_item);
  const discussionTopicSuggestions = suggestions.filter(
    item => item.type === AiSuggestionWorkspaceItemType.discussion_topic
  );

  const handleRefreshSuggestions = () => {
    setSuggestions([]);
    toggleIsRegenerating();
    return disatch(createNewAiSuggestionWorkspaceeItems({ workspaceId })).then(response => {
      if (response.status === 200) {
        setIsReadyForGenerationPoll(true);
      }
    });
  };

  useEffect(() => {
    if (!isReadyForGenerationPoll) return;
    const generationPollInterval = setInterval(() => {
      createNewAiSuggestionWorkspaceeItemsPoll({ workspaceId }).then(({ normalizedData, response }) => {
        if (response.status === 200 && Object.keys(response.data).length) {
          const items = Object.values(normalizedData.aiSuggestionWorkspaceItem).map(({ attributes }) => {
            return { ...attributes };
          });
          setSuggestions(items);
          setIsReadyForGenerationPoll(false);
          toggleIsRegenerating();
        }
        if (response.status !== 200) {
          setIsReadyForGenerationPoll(false);
          toggleIsRegenerating();
        }
      });
    }, 5000);

    return () => clearInterval(generationPollInterval);
  }, [isReadyForGenerationPoll]);

  const handleAcceptAiSuggestion = ({ aiSuggestionSlug }) => {
    return acceptAiSuggestionWorkspaceItem({ workspaceId, aiSuggestionWorkspaceItemSlug: aiSuggestionSlug }).then(
      response => {
        if (response.status === 200) {
          setSuggestions(suggestions.filter(suggestion => suggestion.slug !== aiSuggestionSlug));
          return response;
        }
      }
    );
  };

  const handleRejectAiSuggestion = ({ aiSuggestionSlug }) => {
    return rejectAiSuggestionWorkspaceItem({ workspaceId, aiSuggestionWorkspaceItemSlug: aiSuggestionSlug }).then(
      response => {
        if (response.status === 200) {
          setSuggestions(suggestions.filter(suggestion => suggestion.slug !== aiSuggestionSlug));
          return response;
        }
      }
    );
  };

  useEffect(() => {
    if (!fetchSuggestions) return;
    fetchAiSuggestionWorkspaeItems({ workspaceId })
      .then(({ normalizedData, response }) => {
        if (response.status === 200)
          setSuggestions(
            // @ts-ignore
            Object.values(normalizedData.aiSuggestionWorkspaceItem).map(({ attributes }) => {
              return { ...attributes };
            })
          );
      })
      .finally(() => toggleIsFetching());
  }, [fetchSuggestions]);

  return {
    isFetching,
    isRegenerating,
    suggestions,
    actionItemSuggestions,
    discussionTopicSuggestions,
    handleRefreshSuggestions,
    handleAcceptAiSuggestion,
    handleRejectAiSuggestion,
  };
};
